import React, { useState } from 'react';
import { Card, Col, Row, Skeleton, Space, Statistic, Table, Typography } from 'antd';
import dayjs from 'dayjs';
import { DateRangeFilter, DateRange, defaultDateRange } from '@shared/components/DateRangeFilter';
import { LineChartCard } from '@shared/components/charts/LineChartCard';
import { BarChartCard } from '@shared/components/charts/BarChartCard';
import { useAnalyticsStore } from '@shared/api/hooks/useAnalyticsStore';

export function StorePage(): React.ReactElement {
  const [range, setRange] = useState<DateRange>(defaultDateRange());
  const { data, isLoading } = useAnalyticsStore(range);

  const daily = (data?.daily ?? []).map((r) => ({ ...r, date: dayjs(r.date).format('DD/MM') }));
  const totalRevenue = daily.reduce((s, r) => s + r.revenue, 0);
  const totalOrders = daily.reduce((s, r) => s + r.orders, 0);
  const aov = totalOrders > 0 ? totalRevenue / totalOrders : 0;

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <Typography.Title level={3} style={{ margin: 0, fontStyle: 'italic' }}>
            Vendite Store
          </Typography.Title>
          <Typography.Text type="secondary">Fatturato, ordini e prodotti più venduti</Typography.Text>
        </div>
        <DateRangeFilter value={range} onChange={setRange} />
      </Space>

      {isLoading ? (
        <Skeleton active />
      ) : (
        <>
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col xs={12} sm={8}>
              <Card><Statistic title="Fatturato periodo" value={totalRevenue.toFixed(2)} prefix="€" /></Card>
            </Col>
            <Col xs={12} sm={8}>
              <Card><Statistic title="Ordini periodo" value={totalOrders} /></Card>
            </Col>
            <Col xs={12} sm={8}>
              <Card><Statistic title="Valore medio ordine" value={aov.toFixed(2)} prefix="€" /></Card>
            </Col>
          </Row>

          <Row gutter={[16, 16]}>
            <Col xs={24} lg={12}>
              <LineChartCard
                title="Fatturato e ordini per giorno"
                data={daily}
                xKey="date"
                series={[
                  { key: 'revenue', label: 'Fatturato (€)' },
                  { key: 'orders', label: 'Ordini' },
                ]}
              />
            </Col>
            <Col xs={24} lg={12}>
              <Card title="Prodotti più venduti" style={{ marginBottom: 16 }}>
                <Table
                  size="small"
                  rowKey="productId"
                  dataSource={data?.topProducts ?? []}
                  pagination={false}
                  columns={[
                    { title: 'Prodotto', dataIndex: 'name' },
                    { title: 'Qtà', dataIndex: 'qty', align: 'right' as const, width: 70 },
                    {
                      title: 'Fatturato',
                      dataIndex: 'revenue',
                      align: 'right' as const,
                      width: 110,
                      render: (v: number) => `€${v.toFixed(2)}`,
                    },
                  ]}
                />
              </Card>
            </Col>
          </Row>

          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col xs={24} lg={12}>
              <BarChartCard
                title="Ordini per stato"
                data={(data?.byStatus ?? []).map((r) => ({ status: r.status, count: r.count }))}
                xKey="status"
                series={[{ key: 'count', label: 'Ordini' }]}
              />
            </Col>
            <Col xs={24} lg={12}>
              <BarChartCard
                title="Ordini per stato pagamento"
                data={(data?.byPaymentStatus ?? []).map((r) => ({ status: r.status, count: r.count }))}
                xKey="status"
                series={[{ key: 'count', label: 'Ordini' }]}
              />
            </Col>
          </Row>
        </>
      )}
    </div>
  );
}
